import type { FC } from "react";
import CologuardQualificationNextMoveCard from "components/v3/sharedComponents/NextMoveCard/CologuardQualificationNextMoveCard";
import ReviewResultsNextMoveCard from "components/v3/sharedComponents/NextMoveCard/ReviewResultsNextMoveCard";
import TestingInProgressNextMoveCard from "components/v3/sharedComponents/NextMoveCard/TestingInProgressNextMoveCard";

type NextMoveCardComponent = FC;

const TESTING_IN_PROGRESS = TestingInProgressNextMoveCard;
const REVIEW_RESULTS = ReviewResultsNextMoveCard;
const COLOGUARD_QUALIFICATION = CologuardQualificationNextMoveCard;

/**
 * When the member has lab orders, slot the Cologuard qualification card
 * between unviewed results and testing in progress. After results are viewed,
 * Cologuard moves above testing in progress and viewed results drop below both.
 * With no orders, the list is unchanged.
 */
export const orderActiveNextMoveCards = (
  cards: readonly NextMoveCardComponent[],
  {
    hasOrders,
    hasNewResultsToView
  }: {
    hasOrders: boolean;
    hasNewResultsToView: boolean;
  }
): NextMoveCardComponent[] => {
  if (!hasOrders) {
    return [...cards];
  }

  const testingIndex = cards.indexOf(TESTING_IN_PROGRESS);
  if (testingIndex < 0) {
    return [...cards];
  }

  const preferredOrder = hasNewResultsToView
    ? [REVIEW_RESULTS, COLOGUARD_QUALIFICATION, TESTING_IN_PROGRESS]
    : [COLOGUARD_QUALIFICATION, TESTING_IN_PROGRESS, REVIEW_RESULTS];
  const movable = new Set<NextMoveCardComponent>(preferredOrder);
  const present = preferredOrder.filter((card) => cards.includes(card));
  const stationary = cards.filter((card) => !movable.has(card));
  const insertAt = cards
    .slice(0, testingIndex)
    .filter((card) => !movable.has(card)).length;

  return [
    ...stationary.slice(0, insertAt),
    ...present,
    ...stationary.slice(insertAt)
  ];
};
